import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Tab, Tabs } from '@mui/material'
import { concat, find, flatMap, isEmpty, map, split } from 'lodash'
import { useEffect, useState } from 'react'
import {
  AdoptionDashboardDataResponse,
  AdoptionDashboardFiltersResponse,
  getAdoptionDashboardData as getAdoptionDashboardDataApi,
  getAdoptionDashboardFilters,
} from '../../api'
import EmptyBox from '../../assets/empty_box.svg'
import FetchError from '../../assets/fetch_error.svg'
import { CustomTable } from '../../components/CustomTable'
import { FormFieldLabel } from '../../components/form/FormFieldLabel'
import styles from './AdoptionDashboard.module.css'
import SummaryByOUTab from './SummaryByOUTab'
import ViewEditAdoptionStatusTab from './ViewEditAdoptionStatusTab'

const AdoptionDashboard = () => {
  const [currentTab, setCurrentTab] = useState<'overall' | 'summaryByOU' | 'viewEditStatus'>('overall')

  // --- Overall Summary state ---
  const [periods, setPeriods] = useState<{ value: string; label: string }[] | undefined>(undefined)
  const [selectedPeriod, setSelectedPeriod] = useState<string>('')
  const [adoptionDashboardData, setAdoptionDashboardData] = useState<AdoptionDashboardDataResponse | undefined>(
    undefined,
  )
  const [hasFetchError, setHasFetchError] = useState<boolean>(false)

  // --- Overall Summary logic ---
  const getDefaultPeriod = (periods: AdoptionDashboardFiltersResponse) => {
    const mostRecentYear = Math.max(...periods.map(period => period.year))
    const mostRecentPeriods = periods.filter(period => period.year === mostRecentYear)
    const ytdPeriod = find(mostRecentPeriods, period => period.quarter === 'YTD')
    if (ytdPeriod) {
      return {
        value: `${ytdPeriod.quarter} ${ytdPeriod.year}`,
        label: `${ytdPeriod.quarter} ${ytdPeriod.year}`,
      }
    }
    const latestPeriod = mostRecentPeriods.sort((a, b) => a.quarter.localeCompare(b.quarter))[0]
    return {
      value: `${latestPeriod.quarter} ${latestPeriod.year}`,
      label: `${latestPeriod.quarter} ${latestPeriod.year}`,
    }
  }

  const getAdoptionDashboardData = (quarter: string, year: number) => {
    getAdoptionDashboardDataApi(quarter, year)
      .then(data => {
        if (data) {
          setHasFetchError(false)
          setAdoptionDashboardData(data)
        } else {
          setHasFetchError(true)
        }
      })
      .catch(() => {
        setHasFetchError(true)
      })
  }

  useEffect(() => {
    getAdoptionDashboardFilters().then(data => {
      if (data) {
        let periods = []
        periods = data.map(period => {
          return {
            value: `${period.quarter} ${period.year}`,
            label: `${period.quarter} ${period.year}`,
          }
        })

        const defaultPeriod = getDefaultPeriod(data).value
        setPeriods(periods)
        setSelectedPeriod(defaultPeriod)
        getAdoptionDashboardData(defaultPeriod.split(' ')[0], parseInt(defaultPeriod.split(' ')[1]))
      }
    })
  }, [])

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period)
    getAdoptionDashboardData(period.split(' ')[0], parseInt(period.split(' ')[1]))
  }

  const getTableData = () => {
    const periodsData = flatMap(adoptionDashboardData?.groups, group => {
      const bpdsCells = map(group.bpds, bpd => {
        return {
          hasBackground: false,
          cell: [
            <p className={styles.tableCellTextName}>{bpd.name}</p>,
            <p className={styles.tableCellText}>
              {bpd.launched_in.quarter} {bpd.launched_in.year}
            </p>,
            <p className={styles.tableCellText}>
              {bpd.target_period_percentage ? `${bpd.target_period_percentage}%` : '-'}
            </p>,
            <p className={styles.tableCellText}>
              {bpd.global_adoption_wo_sustainability ? `${bpd.global_adoption_wo_sustainability}%` : '-'}
            </p>,
            <p className={styles.tableCellText}>{bpd.target_percentage ? `${bpd.target_percentage}%` : '-'}</p>,
          ],
        }
      })
      const averageGlobalCells = concat(bpdsCells, {
        hasBackground: true,
        cell: [
          <p className={styles.tableCellTextNameYear}>Average Global Adoption % of {group.year} BPDs</p>,
          <p></p>,
          <p className={styles.tableCellTextYear}>
            {group.avg_global_percentage ? `${group.avg_global_percentage}%` : ''}
          </p>,
          <p className={styles.tableCellTextYear}>
            {group.avg_global_adoption_wo_sustainability ? `${group.avg_global_adoption_wo_sustainability}%` : ''}
          </p>,
          <p className={styles.tableCellTextYear}>
            {group.avg_global_target_percentage ? `${group.avg_global_target_percentage}%` : ''}
          </p>,
        ],
      })
      return averageGlobalCells
    })
    const averageGlobalData = {
      hasBackground: true,
      cell: [
        <p className={styles.tableCellTextNameYear}>
          {`Average Global Adoption % of BPDs Scaled(${adoptionDashboardData?.groups[0].year} - ${split(selectedPeriod, ' ')[1]})`}
        </p>,
        <p></p>,
        <p className={styles.tableCellTextYear}>
          {adoptionDashboardData?.avg_global_percentage_scaled
            ? `${adoptionDashboardData?.avg_global_percentage_scaled}%`
            : ''}
        </p>,
        <p className={styles.tableCellTextYear}>
          {adoptionDashboardData?.avg_global_adoption_wo_sustainability_scaled
            ? `${adoptionDashboardData?.avg_global_adoption_wo_sustainability_scaled}%`
            : ''}
        </p>,
        <p className={styles.tableCellTextYear}>
          {adoptionDashboardData?.avg_global_target_percentage_scaled
            ? `${adoptionDashboardData?.avg_global_target_percentage_scaled}%`
            : ''}
        </p>,
      ],
    }
    return concat(periodsData, averageGlobalData)
  }

  const selectedYear = selectedPeriod ? parseInt(selectedPeriod.split(' ')[1]) : new Date().getFullYear()

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.headerTitle}>Adoption Dashboard</h1>
        <p className={styles.headerDescription}>Best Practice Decoded Adoption Tracker</p>
      </div>
      <div
        className={
          currentTab === 'summaryByOU' || currentTab === 'viewEditStatus' ? styles.subHeaderGrow : styles.subHeader
        }>
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{ borderBottom: 1, borderColor: 'divider', paddingBottom: '16px' }}
            className={styles.tabContainer}>
            <Tabs
              value={currentTab}
              onChange={(_, newValue: 'overall' | 'summaryByOU' | 'viewEditStatus') => setCurrentTab(newValue)}
              classes={{ indicator: styles.tabIndicator }}>
              <Tab
                className={styles.tab}
                label="Overall Summary"
                value="overall"
              />
              <Tab
                className={styles.tab}
                label="SUMMARY BY OU"
                value="summaryByOU"
              />
              <Tab
                className={styles.tab}
                label="VIEW/EDIT ADOPTION STATUS"
                value="viewEditStatus"
              />
            </Tabs>
          </Box>
        </Box>
        {currentTab === 'overall' && (
          <div className={styles.subHeaderContent}>
            <div className={styles.yearFilter}>
              <FormFieldLabel
                text="Year"
                isAiGenerated={false}
              />
              <FormControl fullWidth>
                <Select
                  size="small"
                  value={selectedPeriod}
                  onChange={(e: SelectChangeEvent) => {
                    handlePeriodChange(e.target.value)
                  }}>
                  {map(periods, period => (
                    <MenuItem
                      key={period.label}
                      value={period.value}>
                      {period.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className={styles.keyMetricsCard}>
              <p className={styles.keyMetricsCardText}># of {selectedPeriod} decoded learning.</p>
              <p className={styles.keyMetricsCardValue}>{adoptionDashboardData?.num_new_decoded_learnings ?? '-'}</p>
            </div>
          </div>
        )}
        {currentTab === 'summaryByOU' && <SummaryByOUTab />}
        {currentTab === 'viewEditStatus' && <ViewEditAdoptionStatusTab />}
      </div>

      {currentTab === 'overall' && (
        <>
          <div className={styles.tableContainer}>
            {!isEmpty(adoptionDashboardData?.groups) ? (
              <CustomTable
                headTitles={[
                  'E@S BPDs',
                  'BPD Launched in',
                  `${selectedPeriod}%`,
                  `Adoption w/o sustainability ${selectedPeriod}%`,
                  'Target %',
                ]}
                data={getTableData()}
                isPaginated={false}
                order={undefined}
                orderBy={undefined}
                onRequestSort={() => {}}
                onPageChange={() => {}}
                page={undefined}
                total={undefined}
                hasBorder={true}
                centerHeadTitles={true}
                firstColumnMaxWidth={true}
              />
            ) : (
              <div className={styles.noExperimentsContainer}>
                {hasFetchError ? <img src={FetchError} /> : <img src={EmptyBox} />}
                <p className={styles.noExperimentsHeader}>
                  {hasFetchError ? 'Error Fetching the Requests' : 'No requests found'}
                </p>
                {hasFetchError ? (
                  <p className={styles.noExperimentsText}>
                    There was an issue retrieving the requests. Check your connection and try again. If the error
                    persists, contact the support team.
                  </p>
                ) : (
                  <p className={styles.noExperimentsText}>Try adjusting your search or filter criteria</p>
                )}
              </div>
            )}
          </div>
          {!isEmpty(adoptionDashboardData?.groups) && (
            <div className={styles.tableTextContainer}>
              <p className={styles.tableText}>
                Adoption% on Sustainability BPDs adjusted to reflect Global PACS sustainability communication pause.
              </p>
              <p className={styles.tableText}>
                {' '}
                Global Adoption % is an aggregate metric calculated by dividing the number of eligible markets that have
                activated the BPD by the total number of markets identified as eligible for that BPD.{' '}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default AdoptionDashboard
